import { Buffer } from 'buffer';
import { getTransactionJson } from './solana-transaction-read';
export { getTransactionJson, SOLANA_MAX_SUPPORTED_TRANSACTION_VERSION } from './solana-transaction-read';
import { getConfig } from './config';
const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
/**
 * Minimal base58 encoder, used ONLY to derive a Solana transaction signature
 * from already-signed bytes. Core deliberately does not take a `bs58`
 * dependency for eighteen lines of arithmetic.
 */
export function encodeBase58(bytes) {
    if (bytes.length === 0)
        return '';
    const digits = [0];
    for (const byte of bytes) {
        let carry = byte;
        for (let i = 0; i < digits.length; i++) {
            carry += digits[i] << 8;
            digits[i] = carry % 58;
            carry = (carry / 58) | 0;
        }
        while (carry > 0) {
            digits.push(carry % 58);
            carry = (carry / 58) | 0;
        }
    }
    let out = '';
    for (let i = 0; i < bytes.length && bytes[i] === 0; i++)
        out += BASE58_ALPHABET[0];
    for (let i = digits.length - 1; i >= 0; i--)
        out += BASE58_ALPHABET[digits[i]];
    return out;
}
/**
 * Derive the transaction signature from the SIGNED bytes, before broadcasting.
 *
 * Without this the signature is only learned when `sendRawTransaction` resolves,
 * so an RPC that accepted the bytes but whose response was lost throws with
 * nothing recorded — and a retry builds a DIFFERENT transaction that can land
 * beside the first.
 */
/** true when the base64 wire is a SIMD-0385 v1 transaction (first byte 0x81). */
export function isTransactionV1Base64(serializedTransaction) {
    try {
        const head = Buffer.from(serializedTransaction.slice(0, 4), 'base64');
        return head.length > 0 && head[0] === 0x81;
    }
    catch (_a) {
        return false;
    }
}
export function deriveTransactionSignature(signedTransaction) {
    var _a;
    const signature = (_a = signedTransaction.signatures) === null || _a === void 0 ? void 0 : _a[0];
    if (!signature || signature.length === 0) {
        throw new Error('Signed Solana transaction carries no signature to broadcast');
    }
    if (signature.every(byte => byte === 0)) {
        throw new Error('Signed Solana transaction carries an empty fee-payer signature');
    }
    return encodeBase58(Uint8Array.from(signature));
}
/**
 * Whether a send failure PROVES the transaction was never accepted. A preflight
 * rejection, a signature-verification failure, or an oversized/invalid
 * transaction is definitive and keeps throwing. A transport failure (timeout,
 * socket reset, aborted fetch) is ambiguous — the node may hold the bytes — and
 * must resolve as `submitted` carrying the pre-derived signature.
 *
 * "already processed" is deliberately NOT definitive: it means the signature
 * landed, which is the opposite of a failure.
 */
const DEFINITIVE_SEND_FAILURE_MARKERS = [
    'simulation failed',
    'preflight',
    'blockhash not found',
    'signature verification failure',
    'transaction too large',
    'invalid transaction',
    'insufficient funds for rent',
    'attempt to debit an account but found no record of a prior credit',
];
export function isDefinitiveSolanaSendFailure(error) {
    var _a, _b, _c;
    const err = error;
    if (err && Array.isArray(err.logs))
        return true;
    if (err && String((_a = err.name) !== null && _a !== void 0 ? _a : '') === 'SendTransactionError')
        return true;
    const message = String((_c = (_b = err === null || err === void 0 ? void 0 : err.message) !== null && _b !== void 0 ? _b : err) !== null && _c !== void 0 ? _c : '').toLowerCase();
    if (!message)
        return false;
    if (message.includes('already been processed') || message.includes('already processed'))
        return false;
    return DEFINITIVE_SEND_FAILURE_MARKERS.some(marker => message.includes(marker));
}
// The Bounded program's own rule/invariant rejection, recognized from its
// on-chain logs. On a rule-false the program logs "<Create|Update|Delete> rule
// failed for path ..." then returns Unauthorized<Create|Update|Delete>; a cap /
// declared-invariant breach returns OnchainInvariantViolation. Anchor prints the
// error name + message into the logs even though the client IDL omits these
// codes, so the log text is the reliable signal.
const BOUNDED_RULE_REJECTION_MARKERS = [
    /(?:Create|Update|Delete) rule failed for path/i,
    /Unauthorized(?:Create|Update|Delete)\b/,
    /OnchainInvariantViolation/,
    /invariant postcondition failed/i,
];
function boundedRejectionHaystack(error, extraLogs) {
    const err = error;
    const parts = [];
    for (const source of [err === null || err === void 0 ? void 0 : err.logs, err === null || err === void 0 ? void 0 : err.transactionLogs, extraLogs]) {
        if (Array.isArray(source))
            parts.push(...source.map((line) => String(line)));
        else if (typeof source === 'string')
            parts.push(source);
    }
    if (err === null || err === void 0 ? void 0 : err.message)
        parts.push(String(err.message));
    return parts.join('\n');
}
/**
 * Whether a definitive Solana send/confirm failure is the BOUNDED PROGRAM
 * rejecting the write in simulation - a policy rule or a cap/invariant check -
 * as opposed to an infra failure (blockhash, fees, account not found). Only the
 * former is a stale-mirror race and safe to advertise as retryable.
 */
export function isBoundedOnchainRuleRejection(error, extraLogs) {
    const hay = boundedRejectionHaystack(error, extraLogs);
    return !!hay && BOUNDED_RULE_REJECTION_MARKERS.some((re) => re.test(hay));
}
/**
 * A Bounded on-chain rule/invariant rejection surfaced with a message the caller
 * can act on. This write already PASSED the off-chain preflight (otherwise it
 * would have been declined before a transaction was ever built), so the program
 * disagreeing here means the authoritative on-chain state is ahead of a
 * not-yet-converged read mirror - re-read and retry. If it keeps failing, the
 * rule is genuinely unsatisfied (the cap is truly exceeded, or the write is not
 * authorized). Carries the original error as `cause` and the raw program logs.
 */
export class BoundedOnchainRuleRejection extends Error {
    constructor(cause, extraLogs) {
        super('The Bounded program rejected this write on-chain (a policy rule or cap/invariant check). ' +
            'The off-chain preflight had already passed, so the authoritative on-chain state is ahead of a ' +
            'not-yet-converged read mirror: re-read and retry. If it persists, the rule is genuinely failing ' +
            '(the cap is truly exceeded, or the write is not authorized).');
        this.boundedRuleRejection = true;
        this.retryable = true;
        this.name = 'BoundedOnchainRuleRejection';
        this.cause = cause;
        const err = cause;
        const logs = [];
        for (const source of [err === null || err === void 0 ? void 0 : err.logs, err === null || err === void 0 ? void 0 : err.transactionLogs, extraLogs]) {
            if (Array.isArray(source))
                logs.push(...source.map((line) => String(line)));
        }
        this.logs = logs;
    }
}
export function errorText(error) {
    const message = error === null || error === void 0 ? void 0 : error.message;
    return typeof message === 'string' && message.length > 0 ? message : String(error);
}
const BOUNDED_PROGRAM_MAINNET = ['open', 'Tv7fbpYSseNHYmCZFZ1CZgj4r8D9fKNgEz1qo6F'].join('');
const BOUNDED_PROGRAM_DEVNET = ['open', 'Tv7fbpYSseNHYmCZFZ1CZgj4r8D9fKNgEz1qo6F'].join('');
const COMPUTE_BUDGET_PROGRAM = 'ComputeBudget111111111111111111111111111111';
const SYSTEM_PROGRAM_ID = '11111111111111111111111111111111';
// SPL Associated Token program and the two SPL Token programs it may create an
// account under. The server builder compiles idempotent ATA-create setup
// instructions ahead of the Bounded write for every ATA-creating plugin.
const ASSOCIATED_TOKEN_PROGRAM_ID = 'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL';
const TOKEN_PROGRAM_ID = 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA';
const TOKEN_2022_PROGRAM_ID = 'TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb';
const SPL_TOKEN_PROGRAM_IDS = new Set([TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID]);
// Idempotent ATA create carries a single instruction byte 0x01 (CreateIdempotent)
// and exactly six accounts: funding, ATA, owner, mint, system program, token
// program.
const ATA_CREATE_IDEMPOTENT_DISCRIMINATOR = 1;
const ATA_CREATE_ACCOUNT_COUNT = 6;
// System Program `transfer` is instruction index 2 (u32 LE) followed by a u64
// lamports amount: a 12-byte data payload over exactly two accounts.
const SYSTEM_TRANSFER_INSTRUCTION_INDEX = 2;
const SYSTEM_TRANSFER_DATA_LENGTH = 12;
const SYSTEM_TRANSFER_ACCOUNT_COUNT = 2;
// Gas-sponsorship top-up cap. Mirrors the server builder's ATA-inclusive
// absolute maximum (0.05 SOL); the fee payer (the gas sponsor) can only move its
// OWN lamports up to this bound, and only to a co-signing account.
const SPONSOR_TOPUP_MAX_LAMPORTS = BigInt(50000000);
// The four setup/write programs a server-built transaction may reference. System
// and Associated-Token are typed-validated per instruction below; a bare
// presence in this set is NOT sufficient authorization.
const ALLOWED_SERVER_TX_PROGRAMS = new Set([
    BOUNDED_PROGRAM_MAINNET,
    BOUNDED_PROGRAM_DEVNET,
    COMPUTE_BUDGET_PROGRAM,
    SYSTEM_PROGRAM_ID,
    ASSOCIATED_TOKEN_PROGRAM_ID,
]);
// Canonical set_documents discriminator (single wire format: ra_indices Vec<u8>).
const SET_DOCUMENTS_DISCRIMINATOR = '79,46,72,73,24,79,66,245';
const ALLOWED_BOUNDED_SET_DISCRIMINATORS = new Set([
    SET_DOCUMENTS_DISCRIMINATOR,
]);
class BorshCursor {
    constructor(data, offset, label) {
        this.data = data;
        this.offset = offset;
        this.label = label;
    }
    requireBytes(length, field) {
        if (this.offset + length > this.data.length) {
            throw new Error(`${this.label} has malformed Bounded instruction data while reading ${field}`);
        }
    }
    readU8(field) { this.requireBytes(1, field); return this.data[this.offset++]; }
    readU32(field) {
        this.requireBytes(4, field);
        const value = this.data[this.offset] | (this.data[this.offset + 1] << 8) |
            (this.data[this.offset + 2] << 16) | (this.data[this.offset + 3] << 24);
        this.offset += 4;
        return value >>> 0;
    }
    skip(length, field) { this.requireBytes(length, field); this.offset += length; }
    readBytes(length, field) {
        this.requireBytes(length, field);
        const raw = this.data.slice(this.offset, this.offset + length);
        this.offset += length;
        return raw;
    }
    readString(field) {
        const length = this.readU32(`${field} length`);
        return Buffer.from(this.readBytes(length, field)).toString('utf8');
    }
    readLengthPrefixedBytes(field) {
        return this.readBytes(this.readU32(`${field} length`), field);
    }
    isAtEnd() { return this.offset === this.data.length; }
}
function readBoundedFieldValue(cursor) {
    const option = cursor.readU8('operation value option');
    if (option === 0)
        return null;
    if (option !== 1)
        throw new Error('Server transaction has malformed Bounded field value option');
    const variant = cursor.readU8('operation value variant');
    if (variant === 0)
        return { variant: 'u64', value: readU64LE(cursor.readBytes(8, 'operation numeric value'), 0) };
    if (variant === 1)
        return { variant: 'i64', value: readI64LE(cursor.readBytes(8, 'operation numeric value'), 0) };
    if (variant === 2)
        return { variant: 'bool', value: cursor.readU8('operation bool value') !== 0 };
    if (variant === 3)
        return { variant: 'string', value: cursor.readString('operation string value') };
    if (variant === 4)
        return { variant: 'address', value: cursor.readBytes(32, 'operation address value') };
    throw new Error(`Server transaction has unsupported Bounded field value variant: ${variant}`);
}
function normalizeOnchainPath(path) {
    let normalized = path.startsWith('/') ? path.slice(1) : path;
    if (normalized.endsWith('*') && normalized.length > 1)
        normalized = normalized.slice(0, -1);
    if (normalized.endsWith('/'))
        normalized = normalized.slice(0, -1);
    return normalized;
}
function parseBoundedSetDocumentsInstruction(data, label) {
    if (data.length < 8)
        throw new Error(`${label} has malformed Bounded instruction data`);
    const discriminator = Array.from(data.slice(0, 8)).join(',');
    if (!ALLOWED_BOUNDED_SET_DISCRIMINATORS.has(discriminator)) {
        throw new Error(`${label} contains unsupported Bounded instruction`);
    }
    const cursor = new BorshCursor(data, 8, label);
    const appId = cursor.readString('appId');
    const documents = [];
    const documentCount = cursor.readU32('documents length');
    for (let i = 0; i < documentCount; i++) {
        const path = normalizeOnchainPath(cursor.readString('document path'));
        const operations = [];
        const operationCount = cursor.readU32('operations length');
        for (let j = 0; j < operationCount; j++) {
            const key = cursor.readString('operation key');
            const value = readBoundedFieldValue(cursor);
            const kind = cursor.readU8('operation kind');
            operations.push({ key, value, kind });
        }
        documents.push({ path, operations });
    }
    const deletePaths = [];
    const deleteCount = cursor.readU32('delete paths length');
    for (let i = 0; i < deleteCount; i++)
        deletePaths.push(normalizeOnchainPath(cursor.readString('delete path')));
    const txData = [];
    const txDataCount = cursor.readU32('txData length');
    for (let i = 0; i < txDataCount; i++) {
        txData.push({
            pluginFunctionKey: cursor.readString('txData plugin function key'),
            payload: cursor.readLengthPrefixedBytes('txData bytes'),
            raIndices: cursor.readLengthPrefixedBytes('txData raIndices'),
        });
    }
    const simulateByte = cursor.readU8('simulate');
    if (simulateByte !== 0 && simulateByte !== 1)
        throw new Error(`${label} has malformed Bounded simulate flag`);
    if (!cursor.isAtEnd())
        throw new Error(`${label} has trailing Bounded instruction data`);
    return { appId, documents, deletePaths, txData, simulate: simulateByte === 1 };
}
// ---------------------------------------------------------------------------
// OA-0001: signing-intent binding
//
// The wallet must sign ONLY the state transition the caller actually requested.
// The expected intent below is derived from the CLIENT'S OWN write request - the
// one ground truth a tampered builder response cannot change - and the parsed
// instruction must match it field for field before any signer is invoked.
// ---------------------------------------------------------------------------
/** The program's field-operation kinds (programs/bounded; mirrored by the worker's solana-tx-builder). */
const FIELD_OPERATION_WRITE = 0;
const FIELD_OPERATION_DELETE = 1;
const FIELD_OPERATION_TIMESTAMP = 2;
const FIELD_OPERATION_INCREMENT = 3;
// Keys the worker drops from a client write by NAME before encoding
// (realtime-worker types.ts: SYSTEM_FIELDS without the `_`-prefixed entries the
// client already strips, plus the prototype-pollution DANGEROUS_KEYS). The
// expected model must skip exactly the keys the honest builder skips, or a
// legitimate write would be rejected.
const SERVER_DROPPED_FIELD_KEYS = new Set([
    'pathId',
    'relativePath',
    'absolutePath',
    '__proto__',
    'constructor',
    'prototype',
]);
// The worker resolves {operation:'time',value:'now'} into this sentinel and the
// onchain encoder maps the sentinel to a TIMESTAMP op - including when a caller
// writes the literal string, so the expected model must mirror that quirk.
const TIME_NOW_SENTINEL = '__TIME_NOW__';
/**
 * Build the expected write intent from the exact request the client POSTed
 * (`[{ destinationPath, document }]`, a `null` document being a delete).
 *
 * Each document is JSON-round-tripped first: the server parsed the SERIALIZED
 * body, so the intent must be derived from the same bytes - this drops
 * `undefined` values, turns `NaN`/`Infinity` into `null`, and normalizes key
 * order exactly as the server's JSON.parse did.
 *
 * Operation order mirrors the worker's transform (normalizeWriteBody ->
 * resolveOperationFields -> documentToOperations): plain fields in request key
 * order, then increment fields appended in the order they appeared. The
 * comparison is therefore ORDERED, not a multiset - the honest builder is
 * deterministic given the request, and ordering additionally catches
 * duplicate-path and reordering tampering that the retired set comparison hid.
 */
export function buildExpectedWriteIntent(documents) {
    const intentDocuments = [];
    const deletePaths = [];
    for (const entry of documents) {
        if (entry.document == null) {
            deletePaths.push(entry.destinationPath);
            continue;
        }
        if (typeof entry.document !== 'object') {
            // The worker 400s a scalar document, so no honest 202 can exist for this
            // request. Throw here rather than let a dishonest 202 be compared against
            // an intent this entry never had.
            throw new Error(`Cannot derive the expected write intent for ${entry.destinationPath}: the request document is not an object`);
        }
        const wireDocument = JSON.parse(JSON.stringify(entry.document));
        const operations = [];
        const increments = [];
        for (const [key, value] of Object.entries(wireDocument)) {
            if (key.startsWith('_') || SERVER_DROPPED_FIELD_KEYS.has(key))
                continue;
            if (value === null) {
                operations.push({ key, kind: 'delete' });
                continue;
            }
            if (typeof value === 'object' && !Array.isArray(value) &&
                typeof value.operation === 'string' && value.operation.length > 0) {
                const operation = value.operation;
                if (operation === 'increment' && typeof value.value === 'number') {
                    increments.push({ key, delta: value.value });
                    continue;
                }
                if (operation === 'time' && value.value === 'now') {
                    operations.push({ key, kind: 'timestamp' });
                    continue;
                }
                // Any other {operation,...} shape is rejected by the worker with a 400,
                // so it has no honest onchain encoding. Falling through to a plain write
                // of the raw object keeps the comparison fail-closed: a 202 can only
                // arrive from a dishonest builder, and no instruction can match this.
            }
            if (value === TIME_NOW_SENTINEL) {
                operations.push({ key, kind: 'timestamp' });
                continue;
            }
            operations.push({ key, kind: 'write', value });
        }
        for (const { key, delta } of increments) {
            operations.push({ key, kind: 'increment', delta });
        }
        intentDocuments.push({ path: entry.destinationPath, operations });
    }
    return { documents: intentDocuments, deletePaths };
}
/**
 * Minimal base58 decoder, used ONLY to recognize a declared-Address field: a
 * request string that decodes to exactly the 32 bytes the instruction carries
 * is a faithful AddressVal encoding of that string. Returns null on any
 * non-base58 character.
 */
export function decodeBase58(text) {
    if (text.length === 0)
        return null;
    let value = BigInt(0);
    for (const ch of text) {
        const digit = BASE58_ALPHABET.indexOf(ch);
        if (digit < 0)
            return null;
        value = value * BigInt(58) + BigInt(digit);
    }
    // Leading '1' characters are leading zero bytes, not part of the number.
    let leadingZeros = 0;
    while (leadingZeros < text.length && text[leadingZeros] === BASE58_ALPHABET[0])
        leadingZeros++;
    const body = [];
    if (value > BigInt(0)) {
        let hex = value.toString(16);
        if (hex.length % 2 === 1)
            hex = `0${hex}`;
        for (let i = 0; i < hex.length; i += 2)
            body.push(parseInt(hex.slice(i, i + 2), 16));
    }
    const out = new Uint8Array(leadingZeros + body.length);
    out.set(body, leadingZeros);
    return out;
}
/**
 * Whether `parsed` is an encoding the HONEST builder could have produced for
 * the request field value. The onchain FieldValue variant is chosen by the
 * collection's DECLARED schema (worker-side `jsValueToFieldValue`), which the
 * client does not hold, so every variant any declared type could yield for this
 * JSON value is accepted - and nothing else:
 * - boolean: BoolVal only.
 * - integer number/bigint: U64Val (inference/UInt; UInt takes abs for
 *   negatives) or I64Val (Int), of the exact integer.
 * - fractional number: StringVal of its decimal text (inference/Float), or a
 *   truncated U64/I64 (declared UInt/Int truncates toward zero).
 * - string: StringVal verbatim, AddressVal when it is the base58 of the exact
 *   32 bytes (declared Address), or U64/I64 of its integer text (declared
 *   UInt/Int accept numeric strings, truncating any fraction).
 * - array/object: StringVal of its JSON serialization (identical on both sides
 *   of the wire for the round-tripped value).
 */
function isFaithfulFieldValueEncoding(requestValue, parsed) {
    if (parsed === null)
        return false;
    if (typeof requestValue === 'boolean') {
        return parsed.variant === 'bool' && parsed.value === requestValue;
    }
    if (typeof requestValue === 'number') {
        if (!Number.isFinite(requestValue))
            return false;
        if (Number.isInteger(requestValue)) {
            const exact = BigInt(requestValue);
            if (parsed.variant === 'i64' && parsed.value === exact)
                return true;
            // The declared-UInt encoder stores abs(value), so a negative request
            // integer can honestly arrive as its positive magnitude.
            return parsed.variant === 'u64' && parsed.value === (exact < BigInt(0) ? -exact : exact);
        }
        if (parsed.variant === 'string' && parsed.value === String(requestValue))
            return true;
        const truncated = BigInt(Math.trunc(requestValue));
        if (parsed.variant === 'i64' && parsed.value === truncated)
            return true;
        return parsed.variant === 'u64' && parsed.value === (truncated < BigInt(0) ? -truncated : truncated);
    }
    if (typeof requestValue === 'string') {
        if (parsed.variant === 'string' && parsed.value === requestValue)
            return true;
        if (parsed.variant === 'address') {
            const decoded = decodeBase58(requestValue);
            const expectedBytes = parsed.value;
            if (decoded !== null && decoded.length === 32 && decoded.every((byte, i) => byte === expectedBytes[i]))
                return true;
        }
        if (parsed.variant === 'u64' || parsed.variant === 'i64') {
            const trimmed = requestValue.trim();
            const intPart = trimmed.includes('.') ? trimmed.split('.')[0] : trimmed;
            if (/^-?\d+$/.test(intPart)) {
                const numeric = BigInt(intPart);
                if (parsed.variant === 'i64' && parsed.value === numeric)
                    return true;
                if (parsed.variant === 'u64' && parsed.value === (numeric < BigInt(0) ? -numeric : numeric))
                    return true;
            }
        }
        return false;
    }
    if (requestValue !== null && typeof requestValue === 'object') {
        return parsed.variant === 'string' && parsed.value === JSON.stringify(requestValue);
    }
    return false;
}
const EXPECTED_KIND_TO_WIRE = {
    write: FIELD_OPERATION_WRITE,
    delete: FIELD_OPERATION_DELETE,
    timestamp: FIELD_OPERATION_TIMESTAMP,
    increment: FIELD_OPERATION_INCREMENT,
};
function describeParsedValue(value) {
    if (value === null)
        return 'no value';
    if (value.variant === 'address')
        return `address 0x${Buffer.from(value.value).toString('hex')}`;
    if (value.variant === 'u64' || value.variant === 'i64')
        return `${value.variant} ${value.value.toString()}`;
    return `${value.variant} ${JSON.stringify(value.value)}`;
}
/**
 * OA-0001: the one Bounded set-documents instruction in a server-built
 * transaction must encode EXACTLY the requested write - app id, ordered
 * documents, per-field operation key/kind/value, ordered deletes, and a clear
 * simulate flag - or the transaction is rejected BEFORE the wallet sees it.
 *
 * The comparison is ordered because the honest builder (realtime-worker's
 * solana-tx-builder) preserves the request's document/delete/operation order
 * deterministically; nothing legitimate reorders, and an ordered check catches
 * duplicate-path and reorder tampering the retired path-SET comparison hid.
 *
 * Deliberately NOT bound here: the plugin `txData` entries and the remaining
 * accounts their ra_indices reference. Those are derived by server-side policy
 * hooks (a Jupiter quote, an ATA layout), not by the caller's request, so the
 * client holds no request-derived expectation to compare against; that surface
 * stays bound by the onchain policy, the typed setup-instruction guards, and
 * the attestation co-signer the program demands for any non-empty txData.
 */
function assertMatchesExpectedIntent(label, expectedAppId, expected, parsed) {
    var _a;
    if (parsed.appId !== expectedAppId)
        throw new Error(`${label} Bounded instruction appId does not match configured appId`);
    if (parsed.simulate) {
        throw new Error(`${label} Bounded instruction has the simulate flag set; a client-signed write must be a real execution`);
    }
    if (parsed.deletePaths.length !== expected.deletePaths.length ||
        parsed.deletePaths.some((path, i) => path !== normalizeOnchainPath(expected.deletePaths[i]))) {
        throw new Error(`${label} Bounded instruction delete paths do not match the requested deletes`);
    }
    if (parsed.documents.length !== expected.documents.length) {
        throw new Error(`${label} Bounded instruction document count does not match the requested write ` +
            `(expected ${expected.documents.length}, got ${parsed.documents.length})`);
    }
    for (let i = 0; i < expected.documents.length; i++) {
        const expectedDoc = expected.documents[i];
        const actualDoc = parsed.documents[i];
        const expectedPath = normalizeOnchainPath(expectedDoc.path);
        if (actualDoc.path !== expectedPath) {
            throw new Error(`${label} Bounded instruction document path "${actualDoc.path}" does not match requested path "${expectedPath}"`);
        }
        if (actualDoc.operations.length !== expectedDoc.operations.length) {
            throw new Error(`${label} Bounded instruction operations for path "${expectedPath}" do not match the requested write ` +
                `(expected ${expectedDoc.operations.length} operations, got ${actualDoc.operations.length})`);
        }
        for (let j = 0; j < expectedDoc.operations.length; j++) {
            const expectedOp = expectedDoc.operations[j];
            const actualOp = actualDoc.operations[j];
            if (actualOp.key !== expectedOp.key || actualOp.kind !== EXPECTED_KIND_TO_WIRE[expectedOp.kind]) {
                throw new Error(`${label} Bounded instruction operation ${j} on path "${expectedPath}" does not match the requested write ` +
                    `(expected key "${expectedOp.key}" kind ${expectedOp.kind}, got key "${actualOp.key}" kind ${actualOp.kind})`);
            }
            if (expectedOp.kind === 'delete' || expectedOp.kind === 'timestamp') {
                if (actualOp.value !== null) {
                    throw new Error(`${label} Bounded instruction operation "${expectedOp.key}" on path "${expectedPath}" carries an unexpected value`);
                }
                continue;
            }
            if (expectedOp.kind === 'increment') {
                const expectedDelta = BigInt(Math.trunc(expectedOp.delta));
                if (((_a = actualOp.value) === null || _a === void 0 ? void 0 : _a.variant) !== 'i64' || actualOp.value.value !== expectedDelta) {
                    throw new Error(`${label} Bounded instruction increment "${expectedOp.key}" on path "${expectedPath}" does not match the requested delta`);
                }
                continue;
            }
            if (!isFaithfulFieldValueEncoding(expectedOp.value, actualOp.value)) {
                throw new Error(`${label} Bounded instruction operation "${expectedOp.key}" on path "${expectedPath}" encodes a value that does not match the requested field ` +
                    `(got ${describeParsedValue(actualOp.value)})`);
            }
        }
    }
}
/** Read a little-endian u64 as a BigInt (lamport amounts can exceed 2^53). */
function readU64LE(bytes, offset) {
    let value = BigInt(0);
    for (let i = 0; i < 8; i++)
        value |= BigInt(bytes[offset + i]) << BigInt(8 * i);
    return value;
}
/** Read a little-endian i64 as a BigInt (two's complement). */
function readI64LE(bytes, offset) {
    const unsigned = readU64LE(bytes, offset);
    return unsigned >= (BigInt(1) << BigInt(63)) ? unsigned - (BigInt(1) << BigInt(64)) : unsigned;
}
/**
 * Resolve an instruction's referenced account keys against the transaction's
 * STATIC key list. A setup instruction that reaches into an address-lookup
 * table for any account it needs cannot be validated offline, so it fails
 * closed: every index must resolve to a static key.
 */
function resolveInstructionAccounts(ix, accountKeys, label) {
    return ix.accountKeyIndexes.map(index => {
        if (index >= accountKeys.length) {
            throw new Error(`${label} setup instruction references an address-lookup-table account (not allowed)`);
        }
        return accountKeys[index];
    });
}
/**
 * A server-built idempotent ATA create is authorized ONLY when it is exactly the
 * canonical shape: instruction byte 0x01, six accounts, funded by the
 * transaction fee payer, over the System and an SPL Token program, and where the
 * account being created recomputes to the associated-token-account PDA of its
 * stated owner and mint. Nothing else - no arbitrary account create - passes.
 */
function validateIdempotentAtaCreate(ix, data, accountKeys, feePayer, PublicKeyCtor, label) {
    if (data.length !== 1 || data[0] !== ATA_CREATE_IDEMPOTENT_DISCRIMINATOR) {
        throw new Error(`${label} contains a non-idempotent or malformed associated-token-account instruction`);
    }
    if (ix.accountKeyIndexes.length !== ATA_CREATE_ACCOUNT_COUNT) {
        throw new Error(`${label} associated-token-account create has an unexpected account layout`);
    }
    const [funding, ata, owner, mint, systemProgram, tokenProgram] = resolveInstructionAccounts(ix, accountKeys, label);
    if (!funding.equals(feePayer)) {
        throw new Error(`${label} associated-token-account create is not funded by the transaction fee payer`);
    }
    if (systemProgram.toBase58() !== SYSTEM_PROGRAM_ID) {
        throw new Error(`${label} associated-token-account create references an unexpected system program`);
    }
    if (!SPL_TOKEN_PROGRAM_IDS.has(tokenProgram.toBase58())) {
        throw new Error(`${label} associated-token-account create references an unexpected token program`);
    }
    const [expectedAta] = PublicKeyCtor.findProgramAddressSync([owner.toBuffer(), tokenProgram.toBuffer(), mint.toBuffer()], new PublicKeyCtor(ASSOCIATED_TOKEN_PROGRAM_ID));
    if (!ata.equals(expectedAta)) {
        throw new Error(`${label} associated-token-account create does not derive the canonical account for its owner and mint`);
    }
}
/**
 * A server-built System Program instruction is authorized ONLY as the gas
 * sponsor's top-up: a `transfer` FROM the transaction fee payer (the sponsor),
 * capped at the sponsorship maximum, TO a co-signing account (the sponsored
 * user). The fee payer can therefore only move its OWN lamports, bounded, and
 * only to an account that itself signed the transaction - never a drain to an
 * arbitrary address. Every other System instruction (createAccount, an
 * uncapped or misdirected transfer, ...) is rejected.
 */
function validateSponsorTransfer(ix, data, accountKeys, feePayer, isAccountSigner, label) {
    const isTransfer = data.length === SYSTEM_TRANSFER_DATA_LENGTH &&
        (data[0] | (data[1] << 8) | (data[2] << 16) | (data[3] << 24)) === SYSTEM_TRANSFER_INSTRUCTION_INDEX;
    if (!isTransfer) {
        throw new Error(`${label} contains an unauthorized System Program instruction`);
    }
    if (ix.accountKeyIndexes.length !== SYSTEM_TRANSFER_ACCOUNT_COUNT) {
        throw new Error(`${label} contains an unauthorized System Program instruction`);
    }
    const [from, to] = resolveInstructionAccounts(ix, accountKeys, label);
    if (!from.equals(feePayer)) {
        throw new Error(`${label} System Program transfer must originate from the transaction fee payer (gas sponsor)`);
    }
    const lamports = readU64LE(data, 4);
    if (lamports > SPONSOR_TOPUP_MAX_LAMPORTS) {
        throw new Error(`${label} System Program transfer exceeds the gas-sponsorship cap`);
    }
    const recipientIndex = ix.accountKeyIndexes[1];
    if (!isAccountSigner(recipientIndex) || to.equals(feePayer)) {
        throw new Error(`${label} System Program transfer recipient must be a co-signing sponsored account`);
    }
}
/**
 * The format-blind half of pre-sign validation: every instruction must be a
 * program the server builder is allowed to emit, the two setup shapes must
 * recompute to this write's values, and exactly one Bounded set-documents
 * instruction must bind the caller's own intent (OA-0001). The v0 path feeds it
 * web3.js's compiled message; the SIMD-0385 v1 lane feeds it kit's decoded
 * instruction headers and payloads.
 */
export function validateServerTransactionSemantics(input) {
    const { label, expectedAppId, expectedIntent, accountKeys, isAccountSigner, instructions, PublicKeyCtor } = input;
    if (accountKeys.length === 0)
        throw new Error(`${label} has no static account keys`);
    const feePayer = accountKeys[0];
    let boundedInstructionCount = 0;
    let parsedBoundedInstruction = null;
    for (const ix of instructions) {
        if (!ALLOWED_SERVER_TX_PROGRAMS.has(ix.programId))
            throw new Error(`${label} contains unauthorized program: ${ix.programId}`);
        // Typed setup checks replace the old blanket System-Program ban: each of the
        // two setup shapes the server builder emits (idempotent ATA create, gas
        // sponsor top-up) is bound to recompute to the values this write needs, and
        // anything else fails closed.
        if (ix.programId === SYSTEM_PROGRAM_ID) {
            validateSponsorTransfer(ix, ix.data, accountKeys, feePayer, isAccountSigner, label);
        }
        else if (ix.programId === ASSOCIATED_TOKEN_PROGRAM_ID) {
            validateIdempotentAtaCreate(ix, ix.data, accountKeys, feePayer, PublicKeyCtor, label);
        }
        else if (ix.programId === BOUNDED_PROGRAM_MAINNET || ix.programId === BOUNDED_PROGRAM_DEVNET) {
            boundedInstructionCount++;
            parsedBoundedInstruction = parseBoundedSetDocumentsInstruction(ix.data, label);
        }
    }
    if (boundedInstructionCount !== 1)
        throw new Error(`${label} must contain exactly one Bounded set-documents instruction`);
    // OA-0001: bind the FULL operation semantics of the one Bounded instruction to
    // the caller's own request before any signer runs - not just the app/path
    // envelope, which a tampered builder response can keep while changing amounts.
    assertMatchesExpectedIntent(label, expectedAppId, expectedIntent, parsedBoundedInstruction);
}
function deserializeAndValidateServerTransaction(serializedTransaction, web3, options) {
    const { VersionedTransaction: VersionedTransactionClass, PublicKey: PublicKeyCtor } = web3;
    const transaction = VersionedTransactionClass.deserialize(Buffer.from(serializedTransaction, 'base64'));
    const { label, expectedAppId, expectedIntent } = options;
    const accountKeys = transaction.message.staticAccountKeys;
    const instructions = transaction.message.compiledInstructions.map((ix) => {
        var _a;
        if (ix.programIdIndex >= accountKeys.length)
            throw new Error(`${label} has program ID in lookup table (not allowed)`);
        return {
            programId: accountKeys[ix.programIdIndex].toBase58(),
            accountKeyIndexes: [...ix.accountKeyIndexes],
            data: ix.data instanceof Uint8Array ? ix.data : Buffer.from((_a = ix.data) !== null && _a !== void 0 ? _a : []),
        };
    });
    validateServerTransactionSemantics({
        label,
        expectedAppId,
        expectedIntent,
        accountKeys,
        isAccountSigner: (index) => transaction.message.isAccountSigner(index),
        instructions,
        PublicKeyCtor,
    });
    return transaction;
}
/**
 * DS3-0414: does this server-built transaction already carry a REAL signature?
 *
 * The worker partially signs before it answers: the sponsor keypair fills the
 * fee-payer slot for a sponsored write, and the attestation keypair co-signs any
 * plugin write (realtime-worker solana-tx-builder.ts). A Solana signature covers
 * the compiled message, recentBlockhash INCLUDED, so refreshing the blockhash
 * after either has signed silently voids their signature and the transaction can
 * never land. Versioned signature slots stay all-zero until a signer fills them,
 * and the pre-built lane is always versioned.
 *
 * EVERY slot, not just the fee payer's. The two co-signatures are independent:
 * the sponsor only signs when sponsorship is on, and sponsorship defaults to
 * "none", so the ordinary attested plugin write leaves slot 0 empty for the
 * wallet and carries the worker's signature further down. A fee-payer-only check
 * would wave exactly that shape through into a refresh that voids the
 * attestation signature and makes the write unlandable.
 *
 * Read from the BYTES rather than from the 202's `sponsorSigned` flag, for the
 * same reason: an attested plugin write is co-signed with `sponsorSigned: false`,
 * so the flag is not the question this asks.
 */
function serverTransactionIsPartiallySigned(transaction) {
    return transaction.signatures.some(signature => signature.some(byte => byte !== 0));
}
/**
 * LF-003: hand the wallet a blockhash that is still alive, and report the
 * lifetime the transaction was ACTUALLY signed against.
 *
 * A Solana blockhash dies 150 blocks (~60s) after it is minted, and the worker
 * mints this one at the START of its build. The remaining builder RPC round
 * trips, the response hop, and - dominating everything - the unbounded human
 * pause in front of the wallet's approval dialog all come out of that one
 * window. So the pre-built lane routinely asks a wallet to sign a transaction
 * that is already expired or expires before it can be broadcast: the send fails
 * `blockhash not found` (a DEFINITIVE failure), and a wallet that substitutes
 * the dead blockhash instead of signing it is refused by the SDK's own
 * message-equality check. Refreshing here, immediately before the signature,
 * gives the whole window to the approval.
 *
 * Returns the fence the caller must confirm and reconcile against - refreshing
 * the transaction without moving BOTH fence fields with it would declare expiry
 * against a blockhash the transaction no longer carries.
 */
async function refreshTransactionLifetime(transaction, rpc, serverFence) {
    var _a;
    const fence = { blockhash: serverFence.blockhash, lastValidBlockHeight: serverFence.lastValidBlockHeight };
    // DS3-0414: someone else already signed these exact bytes. Their signature is
    // the constraint; the caller keeps the worker's window - but only once that
    // window is shown to describe the message that was signed. A fence naming a
    // blockhash the transaction does not carry expires on its own schedule, and
    // `expired_not_landed` is the ONE status callers treat as safe to retry, so a
    // disagreement here would turn an in-flight write into a duplicate one.
    if (serverTransactionIsPartiallySigned(transaction)) {
        if (transaction.message.recentBlockhash !== fence.blockhash) {
            throw new Error(`The pre-built Solana transaction was co-signed against blockhash ` +
                `${transaction.message.recentBlockhash} but its expiry fence names ${fence.blockhash}, so the ` +
                'write could not be reconciled honestly. Nothing was signed.');
        }
        return fence;
    }
    // Signing without submitting is allowed to run with no RPC configured at all
    // (the caller broadcasts elsewhere), so there is nothing to refresh from.
    if (!rpc)
        return fence;
    if (typeof rpc.getLatestBlockhash !== 'function') {
        // Fail BEFORE the wallet is opened. Silently signing against the worker's
        // ageing blockhash is the defect this exists to remove, so a Solana RPC that
        // cannot answer is a configuration error, not a fallback.
        throw new Error('Pre-built Solana transaction signing needs a current blockhash, but the configured Solana RPC ' +
            'cannot report one (no getLatestBlockhash). Nothing was signed.');
    }
    const latest = await rpc.getLatestBlockhash('confirmed');
    // Check the SHAPE before writing it onto the message. A value that is not 32
    // base58 bytes cannot be compiled, and web3.js only discovers that inside the
    // layout encoder - by which point the wallet has already been asked for a
    // gesture. It would also become the fence, and a fence nobody can interpret is
    // worse than a stale one.
    const decoded = decodeBase58(String((_a = latest === null || latest === void 0 ? void 0 : latest.blockhash) !== null && _a !== void 0 ? _a : ''));
    if (decoded === null || decoded.length !== 32) {
        throw new Error(`The configured Solana RPC returned "${latest === null || latest === void 0 ? void 0 : latest.blockhash}" as the current blockhash, which is ` +
            'not a 32-byte base58 value. Nothing was signed.');
    }
    transaction.message.recentBlockhash = latest.blockhash;
    return { blockhash: latest.blockhash, lastValidBlockHeight: latest.lastValidBlockHeight };
}
export async function handlePreBuiltTransaction(tx, authProvider, options, expectedIntent) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    // SIMD-0385 v1 wires start with 0x81 (message first, signatures trailing);
    // web3.js's legacy/v0 classes cannot represent them, so they take their own lane.
    if (isTransactionV1Base64(tx.serializedTransaction)) {
        const { handlePreBuiltV1Transaction } = await import('./solana-v1-operations');
        return handlePreBuiltV1Transaction(tx, authProvider, options, expectedIntent);
    }
    const { Connection, VersionedTransaction, PublicKey } = await import("@solana/web3.js");
    // Honor a wallet client's immutable config snapshot for the expected app-id
    // binding and RPC endpoint (CTA-05); otherwise read the global init() config.
    const config = (_b = (_a = options === null || options === void 0 ? void 0 : options._overrides) === null || _a === void 0 ? void 0 : _a._config) !== null && _b !== void 0 ? _b : await getConfig();
    const transaction = deserializeAndValidateServerTransaction(tx.serializedTransaction, { VersionedTransaction, PublicKey }, {
        label: 'Pre-built transaction', expectedAppId: config.appId, expectedIntent,
    });
    const shouldSubmit = (options === null || options === void 0 ? void 0 : options.shouldSubmitTx) !== false;
    const rpcUrl = (_c = config.rpcUrl) === null || _c === void 0 ? void 0 : _c.trim();
    const injectedRpc = (_d = options === null || options === void 0 ? void 0 : options._overrides) === null || _d === void 0 ? void 0 : _d._solanaRpc;
    if (shouldSubmit && !rpcUrl && !injectedRpc) {
        // Checked BEFORE the wallet signs, so a misconfigured app fails its first
        // launch attempt without spending the user's signature. Deliberately no
        // bundled default endpoint (2026-06-28 hardening): the app must supply the
        // RPC it submits through.
        throw new Error(`Pre-built Solana transaction submission requires init({ rpcUrl }) for ${tx.network}. ` +
            `Pass a TOP-LEVEL rpcUrl (with chain) to init(), e.g. ` +
            `init({ appId, chain: '${tx.network}', rpcUrl: '<your ${tx.network} RPC endpoint>' }); ` +
            `a nested walletLogin.rpcUrl only configures wallet login and does not enable submission.`);
    }
    // One RPC handle for the whole lane: the pre-sign lifetime refresh, the
    // broadcast, and the confirmation must all speak to the same endpoint, or the
    // fence would be minted against a chain view the send never sees. Null only on
    // the sign-without-submitting path, which the guard above lets run RPC-less.
    const connection = injectedRpc !== null && injectedRpc !== void 0 ? injectedRpc : (rpcUrl ? new Connection(rpcUrl, 'confirmed') : null);
    // LF-003: the transaction must be signed against a blockhash that is still
    // alive, and the fence must describe the transaction that was actually signed.
    const fence = await refreshTransactionLifetime(transaction, connection, tx);
    const signedTx = await authProvider.signTransaction(transaction);
    const rawTx = signedTx.serialize();
    const signedTransaction = Buffer.from(rawTx).toString('base64');
    if (!shouldSubmit) {
        // Signed and NOT sent. There is no signature and nothing to mirror.
        return { outcome: 'signed', lane: 'solana', transactionSignature: null, signedTransaction, fence };
    }
    // Derive the signature from the signed bytes BEFORE broadcasting, so a send
    // whose response is lost still yields a reconcilable identifier.
    const signature = deriveTransactionSignature(signedTx);
    // Non-null here: the guard above refuses a submitting write with no RPC.
    const submitRpc = connection;
    try {
        await submitRpc.sendRawTransaction(rawTx, { skipPreflight: false, maxRetries: 3 });
    }
    catch (error) {
        // A definitive rejection never entered the network — keep throwing. If it is
        // the Bounded program rejecting the write (rule / cap / invariant), surface
        // the actionable stale-mirror-race message instead of a raw "simulation
        // failed"; otherwise re-throw the infra error unchanged.
        if (isDefinitiveSolanaSendFailure(error)) {
            if (isBoundedOnchainRuleRejection(error))
                throw new BoundedOnchainRuleRejection(error);
            throw error;
        }
        // Ambiguous transport outcome: the node may hold these exact bytes. Report
        // the pre-derived signature so the caller reconciles it instead of building
        // a second transaction that could land beside the first.
        return {
            outcome: 'submitted',
            lane: 'solana',
            transactionSignature: signature,
            signedTransaction,
            fence,
            reason: `Broadcast response was lost or inconclusive: ${errorText(error)}`,
        };
    }
    let confirmation;
    try {
        confirmation = await submitRpc.confirmTransaction({
            signature, blockhash: fence.blockhash, lastValidBlockHeight: fence.lastValidBlockHeight,
        }, 'confirmed');
    }
    catch (error) {
        // The bytes were accepted; only the confirmation outcome is unknown.
        return {
            outcome: 'submitted',
            lane: 'solana',
            transactionSignature: signature,
            signedTransaction,
            fence,
            reason: `Confirmation outcome unknown: ${errorText(error)}`,
        };
    }
    // #310: "confirmed" only means the network recorded the transaction, not that it
    // succeeded - a landed-but-failed transaction carries an err. Report it as a failure
    // (with on-chain logs) instead of a fake success, mirroring the server keypair provider.
    if ((_e = confirmation.value) === null || _e === void 0 ? void 0 : _e.err) {
        let logMessages;
        try {
            const txInfo = await getTransactionJson(submitRpc, signature, 'confirmed');
            logMessages = (_f = txInfo === null || txInfo === void 0 ? void 0 : txInfo.meta) === null || _f === void 0 ? void 0 : _f.logMessages;
        }
        catch (_j) {
            // Log retrieval is diagnostic only; the on-chain error is the verdict.
        }
        if (isBoundedOnchainRuleRejection(confirmation.value.err, logMessages)) {
            throw new BoundedOnchainRuleRejection(confirmation.value.err, logMessages);
        }
        const errorMessage = logMessages ? JSON.stringify(logMessages) : JSON.stringify(confirmation.value.err);
        throw new Error(`Transaction failed: ${errorMessage}`);
    }
    return {
        outcome: 'confirmed',
        lane: 'solana',
        transactionSignature: signature,
        signedTransaction,
        fence,
        confirmationContextSlot: (_h = (_g = confirmation.context) === null || _g === void 0 ? void 0 : _g.slot) !== null && _h !== void 0 ? _h : 0,
    };
}
